import React, { ReactNode, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useStores } from "hooks";
import { Image } from "@carfax/contentful/types/content-type";
import { styles } from "./SampleReport.style";
import { dataLayerEvents } from "domain/SeoAndAnalytics/GoogleAnalytics/dataLayerEvents";
import { Button } from "@carfax/web-ui/elements/Button";
import { ButtonVariants } from "@carfax/web-ui/shared/Button";
import { useLocale } from "next-intl";
import { observer } from "mobx-react";
import { cx } from "../../../styled-system/css";

const MobileReport = dynamic(() => import("./components/MobileReport/MobileReport"), {
  ssr: false,
});

const DesktopReport = dynamic(() => import("./components/DesktopReport/DesktopReport"), {
  ssr: false,
});
interface Props {
  children?: ReactNode;
  optionalDesktopReport?: Image;
  optionalMobileReport?: Image;
  customButtonWrapperStyles?: string;
  withButton?: boolean;
  buttonText?: string;
  buttonVariant?: ButtonVariants;
  inline?: boolean;
}

const SampleReport = observer(
  ({
    children,
    optionalDesktopReport,
    optionalMobileReport,
    customButtonWrapperStyles,
    withButton = false,
    buttonText,
    buttonVariant = ButtonVariants.Outlined,
    inline = false,
  }: Props) => {
    "use no memo";
    const { browserStore } = useStores();
    const { sampleReportStore } = useStores();
    const [sampleReportModalOpen, setSampleReportModalOpen] = useState(false);
    const [sampleReportMobileOpen, setSampleReportMobileOpen] = useState(false);
    const locale = useLocale();

    const onClickSampleReportLink = (event: React.MouseEvent) => {
      event.preventDefault();
      if (browserStore.isTabletAndLarger) {
        setSampleReportModalOpen(true);
      } else if (!browserStore.isTabletAndLarger) {
        setSampleReportMobileOpen(!sampleReportMobileOpen);
        sampleReportStore.toggleSampleReportMobileOpen();
      }

      dataLayerEvents.submitViewSampleReportEvent();
    };

    useEffect(() => {
      const setSampleReport = async () => {
        const ContentfulApi = (await import("@carfax/contentful/contentful-api.class")).default;
        const defaultSampleReports = await ContentfulApi.getInstance().getOptionalAssets(locale);
        sampleReportStore.setSampleReport(defaultSampleReports);
      };
      if (sampleReportMobileOpen || sampleReportModalOpen) {
        setSampleReport();
      }
    }, [sampleReportMobileOpen, sampleReportModalOpen]);
    if (inline) {
      return (
        <>
          <Button
            testSelector="sample-report-open"
            onClick={(event) => onClickSampleReportLink(event)}
            variant={buttonVariant}
            className={customButtonWrapperStyles}
          >
            {buttonText}
          </Button>
          {sampleReportMobileOpen && sampleReportStore.defaultSampleReport && (
            <MobileReport
              optionalMobileReport={optionalMobileReport}
              defaultSampleReports={sampleReportStore.defaultSampleReport}
            />
          )}
          {sampleReportModalOpen && sampleReportStore.defaultSampleReport && (
            <DesktopReport
              sampleReportModalOpen={sampleReportModalOpen}
              setSampleReportModalOpen={setSampleReportModalOpen}
              optionalDesktopReport={optionalDesktopReport}
              defaultSampleReports={sampleReportStore.defaultSampleReport}
            />
          )}
        </>
      );
    }

    return (
      <div>
        {!withButton && children && (
          <button
            className={styles.accessibleButton()}
            data-testid="sample-report-open"
            onClick={(event) => onClickSampleReportLink(event)}
          >
            <div className={cx(styles.noVisibleButtonWrapper(), customButtonWrapperStyles)}>
              <>{children}</>
            </div>
          </button>
        )}
        {withButton && (
          <div className={cx(styles.buttonWrapper(), customButtonWrapperStyles)}>
            <Button
              testSelector="sample-report-open"
              onClick={(event) => onClickSampleReportLink(event)}
              variant={buttonVariant}
            >
              {buttonText}
            </Button>
          </div>
        )}
        {sampleReportMobileOpen && sampleReportStore.defaultSampleReport && (
          <MobileReport
            optionalMobileReport={optionalMobileReport}
            defaultSampleReports={sampleReportStore.defaultSampleReport}
          />
        )}
        {sampleReportModalOpen && sampleReportStore.defaultSampleReport && (
          <DesktopReport
            sampleReportModalOpen={sampleReportModalOpen}
            setSampleReportModalOpen={setSampleReportModalOpen}
            optionalDesktopReport={optionalDesktopReport}
            defaultSampleReports={sampleReportStore.defaultSampleReport}
          />
        )}
      </div>
    );
  },
);

export default SampleReport;
